import axios from 'axios'
import { resolveAuthHeaderValue } from '@/utils/authToken'
import { apiUrl } from '@/utils/appConfig'

const wordpressVisitsApiClient = axios.create({
  baseURL: apiUrl(),
})

wordpressVisitsApiClient.interceptors.request.use(config => {
  const authHeaderValue = resolveAuthHeaderValue()
  if (!authHeaderValue) return config

  const nextConfig = { ...config }
  if (!nextConfig.headers) nextConfig.headers = {}
  nextConfig.headers.Authorization = authHeaderValue
  return nextConfig
})

const toNumber = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const toNullableNumber = value => {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const toStringValue = value => String(value == null ? '' : value).trim()

const toDateOnlyString = value => {
  const raw = toStringValue(value)
  if (!raw) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return raw
  return date.toISOString().slice(0, 10)
}

const toNullableDateOnlyString = value => {
  if (value === null || value === undefined || value === '') return null
  return toDateOnlyString(value)
}

const normalizeVisitPoint = payload => ({
  date: toDateOnlyString(payload?.date),
  uniqueVisitors: toNumber(payload?.uniqueVisitors),
  visits: toNumber(payload?.visits),
  usersCount: toNumber(payload?.usersCount),
  actions: toNumber(payload?.actions),
  maxActions: toNumber(payload?.maxActions),
  bounceCount: toNumber(payload?.bounceCount),
  visitsConverted: toNumber(payload?.visitsConverted),
  sumVisitLength: toNumber(payload?.sumVisitLength),
  avgTimeOnSite: toNumber(payload?.avgTimeOnSite),
  bounceRatePercent: toNumber(payload?.bounceRatePercent),
})

const normalizeVisitTotals = payload => ({
  uniqueVisitors: toNumber(payload?.uniqueVisitors),
  visits: toNumber(payload?.visits),
  usersCount: toNumber(payload?.usersCount),
  actions: toNumber(payload?.actions),
  maxActions: toNumber(payload?.maxActions),
  bounceCount: toNumber(payload?.bounceCount),
  visitsConverted: toNumber(payload?.visitsConverted),
  sumVisitLength: toNumber(payload?.sumVisitLength),
  avgTimeOnSite: toNumber(payload?.avgTimeOnSite),
  bounceRatePercent: toNumber(payload?.bounceRatePercent),
})

const normalizeVisitChangePercent = payload => ({
  uniqueVisitors: toNullableNumber(payload?.uniqueVisitors),
  visits: toNullableNumber(payload?.visits),
  usersCount: toNullableNumber(payload?.usersCount),
  actions: toNullableNumber(payload?.actions),
  maxActions: toNullableNumber(payload?.maxActions),
  bounceCount: toNullableNumber(payload?.bounceCount),
  visitsConverted: toNullableNumber(payload?.visitsConverted),
  sumVisitLength: toNullableNumber(payload?.sumVisitLength),
  avgTimeOnSite: toNullableNumber(payload?.avgTimeOnSite),
  bounceRatePercent: toNullableNumber(payload?.bounceRatePercent),
})

const normalizeVisitDynamics = payload => ({
  previousFromDate: toDateOnlyString(payload?.previousFromDate),
  previousToDate: toDateOnlyString(payload?.previousToDate),
  currentTotals: normalizeVisitTotals(payload?.currentTotals),
  previousTotals: normalizeVisitTotals(payload?.previousTotals),
  changePercent: normalizeVisitChangePercent(payload?.changePercent),
})

const normalizeDateRangeParams = ({ fromDate = '', toDate = '' } = {}) => {
  const params = {}
  if (toStringValue(fromDate)) params.fromDate = toDateOnlyString(fromDate)
  if (toStringValue(toDate)) params.toDate = toDateOnlyString(toDate)
  return params
}

const normalizeSingleSyncResponse = payload => ({
  streamId: toNumber(payload?.streamId),
  fromDate: toDateOnlyString(payload?.fromDate),
  toDate: toDateOnlyString(payload?.toDate),
  requestedDays: toNumber(payload?.requestedDays),
  syncedDays: toNumber(payload?.syncedDays),
  failedDays: toNumber(payload?.failedDays),
  errors: Array.isArray(payload?.errors) ? payload.errors.map(item => toStringValue(item)).filter(Boolean) : [],
})

const normalizeBulkSyncItem = payload => ({
  streamId: toNumber(payload?.streamId),
  streamName: toStringValue(payload?.streamName),
  status: toStringValue(payload?.status).toUpperCase(),
  message: toStringValue(payload?.message) || null,
  fromDate: toNullableDateOnlyString(payload?.fromDate),
  toDate: toNullableDateOnlyString(payload?.toDate),
  requestedDays: toNullableNumber(payload?.requestedDays),
  syncedDays: toNullableNumber(payload?.syncedDays),
  failedDays: toNullableNumber(payload?.failedDays),
})

const normalizeBulkSyncResponse = payload => {
  const items = Array.isArray(payload?.items) ? payload.items.map(normalizeBulkSyncItem) : []
  return {
    fromDate: toDateOnlyString(payload?.fromDate),
    toDate: toDateOnlyString(payload?.toDate),
    totalWordPressStreams: toNumber(payload?.totalWordPressStreams),
    eligibleStreams: toNumber(payload?.eligibleStreams),
    syncedStreams: toNumber(payload?.syncedStreams),
    failedStreams: toNumber(payload?.failedStreams),
    skippedStreams: toNumber(payload?.skippedStreams),
    items,
  }
}

const normalizeTotalChartResponse = payload => {
  const points = Array.isArray(payload?.points) ? payload.points.map(normalizeVisitPoint) : []
  return {
    fromDate: toDateOnlyString(payload?.fromDate),
    toDate: toDateOnlyString(payload?.toDate),
    points,
    hasDynamics: Boolean(payload?.dynamics),
    dynamics: normalizeVisitDynamics(payload?.dynamics),
  }
}

const normalizeStreamStatsResponse = payload => ({
  streamId: toNumber(payload?.streamId),
  streamName: toStringValue(payload?.streamName),
  fromDate: toDateOnlyString(payload?.fromDate),
  toDate: toDateOnlyString(payload?.toDate),
  points: Array.isArray(payload?.points) ? payload.points.map(normalizeVisitPoint) : [],
  hasDynamics: Boolean(payload?.dynamics),
  dynamics: normalizeVisitDynamics(payload?.dynamics),
})

const normalizeStreamsChartResponse = payload => {
  const streams = Array.isArray(payload?.streams)
    ? payload.streams.map(stream => ({
      streamId: toNumber(stream?.streamId),
      streamName: toStringValue(stream?.streamName),
      points: Array.isArray(stream?.points) ? stream.points.map(normalizeVisitPoint) : [],
    }))
    : []

  return {
    fromDate: toDateOnlyString(payload?.fromDate),
    toDate: toDateOnlyString(payload?.toDate),
    streams,
  }
}

export async function syncWordpressVisitsByStream({ streamId } = {}) {
  const params = { streamId: Number(streamId) }

  const { data } = await wordpressVisitsApiClient.post('/api/wordpress-visits-summary/sync', null, { params })
  return normalizeSingleSyncResponse(data)
}

export async function syncAllWordpressVisits() {
  const { data } = await wordpressVisitsApiClient.post('/api/wordpress-visits-summary/sync/all')
  return normalizeBulkSyncResponse(data)
}

export async function getWordpressVisitsStreamStats(streamId, { fromDate = '', toDate = '' } = {}) {
  const params = normalizeDateRangeParams({ fromDate, toDate })
  const encodedStreamId = encodeURIComponent(String(streamId))
  const { data } = await wordpressVisitsApiClient.get(`/api/wordpress-visits-summary/stream/${encodedStreamId}`, { params })
  const response = normalizeStreamStatsResponse(data)
  response.points = response.points.sort((a, b) => a.date.localeCompare(b.date))
  return response
}

export async function getWordpressVisitsTotalChart({ fromDate = '', toDate = '' } = {}) {
  const params = normalizeDateRangeParams({ fromDate, toDate })
  const { data } = await wordpressVisitsApiClient.get('/api/wordpress-visits-summary/charts/total', { params })
  return normalizeTotalChartResponse(data)
}

export async function getWordpressVisitsStreamsChart({ fromDate = '', toDate = '' } = {}) {
  const params = normalizeDateRangeParams({ fromDate, toDate })
  const { data } = await wordpressVisitsApiClient.get('/api/wordpress-visits-summary/charts/streams', { params })
  return normalizeStreamsChartResponse(data)
}
